import { SymbologyValues } from 'app/store/appSettings/AppSettingsState';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSymbolsService } from './useSymbolsService';
import { useLogger } from 'logging/writers/hooks';
import { useDebounce } from 'use-debounce';

interface UseSymbolValidationRequest {
    symbol: string | undefined;
    symbology: SymbologyValues;
}

export interface UseSymbolValidationResponse {
    symbol: string | undefined;
    symbology: SymbologyValues;
    valid: boolean;
    matches: string[];
}

export function useSymbolValidation(
    symbolP: string | undefined,
    symbology: SymbologyValues
): {
    symbolInvalid: boolean;
    result: UseSymbolValidationResponse;
} {
    const [symbol] = useDebounce(symbolP, 500);
    const [result, setResult] = useState<UseSymbolValidationResponse>({
        symbol,
        symbology,
        valid: true,
        matches: [],
    });
    const service = useSymbolsService();
    const request = useRef<UseSymbolValidationRequest>({ symbol, symbology });
    const logger = useLogger('useSymbolValidation');

    useEffect(() => {
        let disposed = false;

        (async () => {
            request.current = { symbol, symbology };

            if (!symbol) {
                setResult({
                    symbol,
                    symbology,
                    valid: true,
                    matches: result.matches,
                });
                return;
            }

            if (
                result.matches.includes(symbol) &&
                result.symbology === symbology
            ) {
                setResult({
                    symbol,
                    symbology,
                    valid: true,
                    matches: result.matches,
                });
                return;
            }

            try {
                const response = await service.validateSymbol(
                    symbol,
                    symbology
                );

                if (disposed) {
                    return;
                }

                const matches: string[] =
                    response.matches && response.matches[symbology]
                        ? response.matches[symbology].map((s) => s.symbol)
                        : [];

                if (
                    response.symbol === request.current.symbol &&
                    response.symbology === request.current.symbology
                ) {
                    setResult({
                        symbol: response.symbol,
                        symbology: response.symbology,
                        valid: response.valid,
                        matches,
                    });
                }
            } catch (err) {
                logger.warn('Error from symbol search API', {
                    request: request.current,
                    err,
                });
            }

            return () => (disposed = false);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [symbol, symbology, service, logger]);

    const symbolInvalid =
        result &&
        !result.valid &&
        result.symbol === symbol &&
        result.symbology === symbology;

    return useMemo(() => ({ symbolInvalid, result }), [symbolInvalid, result]);
}
